import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import API from "../api";

/* ════════════════════════════════════════════════════════════
   FIX: Image/asset URLs must be resolved against the BACKEND's
   origin (e.g. https://ezylab.in/backend), not the frontend's
   own origin. Previously assetUrl()/headerLogoUrl() returned
   root-relative paths like "/uploads/settings/xxx.png", which
   the browser resolved against the React app's own origin
   (https://ezylab.in/uploads/...) instead of the backend
   (https://ezylab.in/backend/uploads/...) where the files are
   actually served from (Express: app.use('/uploads', ...)).
   Since nginx only proxies /backend/* to Node, the root-level
   request 404'd and the header image never loaded.

   API_BASE is pulled from the same axios instance already used
   for all other requests, so it always matches VITE_API_BASE_URL
   without hardcoding the domain here.
   ════════════════════════════════════════════════════════════ */
const API_BASE = (API.defaults.baseURL || "")
  .replace(/\/+$/, "")
  .replace(/\/api$/, "");

const BarcodeDisplay: React.FC<{ value: string; width?: number; height?: number }> = ({
  value,
  width = 220,
  height = 38,
}) => {
  const bars: { x: number; w: number; filled: boolean }[] = [];
  const barUnit = 1.8;
  let x = 2;

  [true, false, true].forEach(f => { bars.push({ x, w: barUnit, filled: f }); x += barUnit + 0.4; });
  x += barUnit * 0.5;

  for (let i = 0; i < value.length; i++) {
    const code = value.charCodeAt(i);
    for (let b = 6; b >= 0; b--) {
      const filled = Boolean((code >> b) & 1);
      const bw = barUnit * (filled ? 1.2 : 0.7);
      bars.push({ x, w: bw, filled });
      x += bw + 0.3;
    }
    x += barUnit * 0.6;
  }

  [false, true, false, true].forEach(f => { bars.push({ x, w: barUnit, filled: f }); x += barUnit + 0.4; });
  const totalWidth = x + 4;

  return (
    <div style={{ textAlign: "center" }}>
      <svg
        viewBox={`0 0 ${totalWidth} ${height}`}
        width={width}
        height={height}
        xmlns="http://www.w3.org/2000/svg"
        style={{ display: "block", margin: "0 auto" }}
        preserveAspectRatio="none"
      >
        {bars.map((bar, i) =>
          bar.filled
            ? <rect key={i} x={bar.x} y={0} width={bar.w} height={height} fill="#000" />
            : null
        )}
      </svg>
    </div>
  );
};

// Style the org's Settings page can save for High/Low flagged results.
type ResultAlertType = "bold" | "color" | "text" | "arrow";

interface LabInfo {
  id?: number;
  lab_name?: string;
  location?: string;
  phone?: string;
  email?: string;
  report_footer?: string;
  logo?: string;
  header_logo_1?: string;
  header_logo_2?: string;
  report_header?: string;
  show_report_header?: number;

  // ── Result Alert ─────────────────────────────────────────────────
  enable_result_alert?: boolean;
  result_alert_type?: ResultAlertType;
  result_alert_color?: string;
}

interface BillData {
  id?: number;
  bill_no?: string;
  patient_id?: number;
  net_amount?: number;
  advance_amount?: number;
  entry_date?: string;
  entry_time?: string;
  created?: string;
  modified?: string;
  // Time component of the saved report timestamp.
  modified_time?: string;
  ref_prefix?: string;
  reference?: string;
  test_status?: number;
}

interface Patient {
  id?: number;
  prefix?: string;
  P_name?: string;
  P_code?: string;
  age?: string;
  age_type?: string;
  gender?: string;
  mobile?: string;
  address?: string;
  id_card_type?: string;
  id_number?: string;
}

interface TestInfo {
  test_name?: string;
  units?: string;
  lower_limit?: string;
  upper_limit?: string;
  type_test?: string;
  table_input?: string;
  sample_type?: string;
  method?: string;
  show_critical_info?: number;
  critical_info?: string;
  show_interpretation?: number;
  interpretation?: string;
  comments?: string;
}

interface TestEntry {
  id?: number;
  test_id?: number;
  sub_heading?: string;
  test_result?: string;
  paragraph?: string;
  date?: string;
  time?: string;
  head_1?: string; head_2?: string; head_3?: string;
  head_4?: string; head_5?: string; head_6?: string;
  result_1?: string; result_2?: string; result_3?: string;
  result_4?: string; result_5?: string; result_6?: string;
  test_info?: TestInfo;
  images?: { document_id: number; document_path: string }[];
}

interface CategoryGroup {
  category_name?: string;
  tests?: TestEntry[];
}

interface ReceiptData {
  labInfo?: LabInfo;
  orgInfo?: LabInfo;
  billData?: BillData;
  reference?: string;
  patient?: Patient;
  terms?: { id: number; description: string }[];
  entries?: CategoryGroup[];
  headerMode?: string;
}

const safe = (v: any, fallback = "") =>
  v !== null && v !== undefined && v !== "" ? String(v) : fallback;

/* ════════════════════════════════════════════════════════════
   FIXED: normalize the raw path first, THEN prefix it with the
   backend's origin (API_BASE) so the browser requests it from
   the backend server, not the frontend's own origin.
   ════════════════════════════════════════════════════════════ */
const normalizeAssetPath = (p: string): string => {
  const s = p.trim();
  if (s.startsWith("/")) return s;
  if (s.startsWith("uploads/")) return `/${s}`;
  if (s.startsWith("logo/")) return `/${s}`;
  if (s.startsWith("settings_")) return `/uploads/settings/${s}`;
  if (s.includes("/")) return `/uploads/${s}`;
  return `/uploads/settings/${s}`;
};

const normalizeHeaderLogoAssetPath = (p: string): string => {
  const s = p.trim();
  if (s.startsWith("/")) return s;
  if (s.startsWith("uploads/")) return `/${s}`;
  if (s.startsWith("logo/")) return `/${s}`;
  if (s.startsWith("settings_")) return `/uploads/settings/${s}`;
  if (s.includes("/")) return `/uploads/${s}`;
  return `/uploads/settings/${s}`;
};

const assetUrl = (p?: string): string => {
  if (!p) return "";
  const s = p.trim();
  if (!s) return "";
  if (/^https?:\/\//i.test(s)) return s;
  return `${API_BASE}${normalizeAssetPath(s)}`;
};

const headerLogoUrl = (p?: string): string => {
  if (!p) return "";
  const s = p.trim();
  if (!s) return "";
  if (/^https?:\/\//i.test(s)) return s;
  return `${API_BASE}${normalizeHeaderLogoAssetPath(s)}`;
};

const getResultFlag = (result?: string, lower?: string, upper?: string): "H" | "L" | null => {
  if (!result) return null;

  const resMatch = String(result).trim().match(/-?\d+(?:\.\d+)?/);
  if (!resMatch) return null;
  const num = parseFloat(resMatch[0]);
  if (isNaN(num)) return null;

  let lo: number | null = null;
  let hi: number | null = null;

  const lowStr = String(lower ?? "").trim();
  const upStr  = String(upper ?? "").trim();

  // Support single range strings in lower_limit (e.g. "70 - 110" or "70-110")
  const rangeMatch = lowStr.match(/(-?\d+(?:\.\d+)?)\s*[-–—]\s*(-?\d+(?:\.\d+)?)/);
  if (rangeMatch) {
    lo = parseFloat(rangeMatch[1]);
    hi = parseFloat(rangeMatch[2]);
  } else {
    if (lowStr) {
      const m = lowStr.match(/-?\d+(?:\.\d+)?/);
      if (m) lo = parseFloat(m[0]);
    }
    if (upStr) {
      const m = upStr.match(/-?\d+(?:\.\d+)?/);
      if (m) hi = parseFloat(m[0]);
    }
  }

  if (hi !== null && !isNaN(hi) && num > hi) return "H";
  if (lo !== null && !isNaN(lo) && num < lo) return "L";
  return null;
};

/* ════════════════════════════════════════════════════════════
   NEW: Result Alert renderer.

   Previously `flag` / `dropdownFlag` / `subFlag` were computed via
   getResultFlag() but never actually used anywhere in the render —
   High/Low results printed with no visual distinction at all. This
   applies whichever style the lab picked on the Settings page
   (Bold / Color / Text "H"/"L" / Arrow ▲▼) to a flagged value, and
   is a no-op (returns the value unchanged) when the alert is
   disabled or the value isn't flagged.
   ════════════════════════════════════════════════════════════ */
const DEFAULT_ALERT_COLOR = "#d00000";

const renderFlaggedResult = (
  value: React.ReactNode,
  flag: "H" | "L" | null,
  lab: LabInfo
): React.ReactNode => {
  if (!flag || !lab.enable_result_alert || value === "" || value === null || value === undefined) {
    return value;
  }

  const alertType = lab.result_alert_type || "text";
  const color = lab.result_alert_color || DEFAULT_ALERT_COLOR;

  switch (alertType) {
    case "bold":
      return <strong>{value}</strong>;

    case "color":
      return <span style={{ color, fontWeight: 700 }}>{value}</span>;

    case "arrow":
      return (
        <span style={{ color, fontWeight: 700, whiteSpace: "nowrap" }}>
          {value} {flag === "H" ? "\u25B2" : "\u25BC"}
        </span>
      );

    case "text":
    default:
      return (
        <span>
          {value}
          <sup style={{ fontWeight: 800, marginLeft: 2 }}>{flag}</sup>
        </span>
      );
  }
};

const fmtDate = (d?: string) => {
  if (!d) return "";
  try {
    // Handle "YYYY-MM-DD" or "YYYY-MM-DD HH:MM:SS" — extract date part only
    // to avoid UTC→local timezone shift when constructing a Date object.
    const dateStr = String(d).trim();
    const isoDate = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (isoDate) {
      // Use parts directly: no timezone conversion needed
      return `${isoDate[3]}-${isoDate[2]}-${isoDate[1]}`;
    }
    return new Date(dateStr)
      .toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" })
      .replace(/\//g, "-");
  } catch { return String(d); }
};

const fmtTime = (d?: string) => {
  if (!d) return "";
  const s = String(d).trim();
  // Match pure time strings like "06:39" or "06:39:00"
  const timeOnly = s.match(/^(\d{1,2}):(\d{1,2})(?::(\d{2}))?$/);
  if (timeOnly) {
    const h = parseInt(timeOnly[1], 10);
    const m = timeOnly[2].padStart(2, '0');
    const suffix = h >= 12 ? "PM" : "AM";
    const h12 = h % 12 === 0 ? 12 : h % 12;
    return `${String(h12).padStart(2, '0')}:${m} ${suffix}`;
  }
  // If it looks like a datetime string, extract the time part directly
  // without constructing a Date object (avoids UTC offset issues).
  const dtMatch = s.match(/(\d{1,2}):(\d{1,2})(?::(\d{2}))?(?:\s*(AM|PM))?$/i);
  if (dtMatch) {
    let h = parseInt(dtMatch[1], 10);
    const m = dtMatch[2].padStart(2, '0');
    const meridiem = dtMatch[4]?.toUpperCase();
    if (meridiem === "PM" && h < 12) h += 12;
    if (meridiem === "AM" && h === 12) h = 0;
    const suffix = h >= 12 ? "PM" : "AM";
    const h12 = h % 12 === 0 ? 12 : h % 12;
    return `${String(h12).padStart(2, '0')}:${m} ${suffix}`;
  }
  return "";
};

const BORDER = "1px solid #cfd4da";
const ACCENT = "#000";
const HEAD_BG = "#F5F5F5";

const BASE_FONT: React.CSSProperties = {
  fontFamily: "Arial, Helvetica, sans-serif",
  fontSize: 12,
};

const COLW = {
  sample: "12%",
  investigation: "34%",
  result: "23%",
  units: "12%",
  range: "19%",
};

const tdBase: React.CSSProperties = {
  ...BASE_FONT,
  border: BORDER,
  padding: "3px 8px",
  verticalAlign: "middle",
  textAlign: "left",
  lineHeight: 1.3,
  wordBreak: "break-word",
  overflowWrap: "anywhere",
};

const thBase: React.CSSProperties = {
  ...tdBase,
  background: HEAD_BG,
  fontWeight: 700,
  textAlign: "left",
  color: ACCENT,
  padding: "5px 8px",
};

const labelCell: React.CSSProperties = {
  ...tdBase,
  width: "16%",
  fontWeight: 700,
  whiteSpace: "nowrap",
};

const label1Cell: React.CSSProperties = {
  ...tdBase,
  width: "16%",
  fontWeight: 400,
  whiteSpace: "nowrap",
};

const valueCell: React.CSSProperties = {
  ...tdBase,
  width: "34%",
};

const methodSubText: React.CSSProperties = {
  fontSize: 9,
  fontStyle: "italic",
  color: "#555",
  marginTop: 1,
  lineHeight: 1.1,
};

const A4_WIDTH_MM  = 210;
const A4_HEIGHT_MM = 297;

/* ════════════════════════════════════════════════════════════
   Per-page print margins for pre-printed letterhead stock.

   When no header image/text is selected (headerMode === "0"),
   the lab is printing onto paper that ALREADY has a letterhead
   (logo/address block) pre-printed at the top and a footer
   pre-printed at the bottom. A single fixed @page margin isn't
   enough — this must apply on EVERY page since @page margins
   apply per page, not just the first, and content must not
   collide with the pre-printed header/footer on any page of a
   multi-page report.

   Used both by the desktop print path (openPrintDialog, via
   @page CSS) and by the PDF generator (buildReportPdf, applied
   directly as jsPDF page margins) so both outputs match.
   ════════════════════════════════════════════════════════════ */
const NO_HEADER_PAGE_MARGIN   = "30mm 10mm 30mm 10mm"; // top right bottom left — tuned so the FINAL printed result lands at 4cm top/bottom (browsers add their own extra default print margin on top of this @page value, so this is intentionally less than 40mm)
const WITH_HEADER_PAGE_MARGIN = "16mm 10mm 14mm 10mm"; // original tighter margin when we render our own header

/* ════════════════════════════════════════════════════════════
   WhatsApp share message. Edit the phone number / feedback link
   here — used by the Share button below.
   ════════════════════════════════════════════════════════════ */
const WHATSAPP_SHARE_MESSAGE =
  "Healthcare Diagnostic Centre\n" +
  "Dear Sir/Madam,\n" +
  "Your lab report is ready. You may collect it from our centre. For any queries, please contact us:\n" +
  "8903263899\n" +
  "⭐⭐⭐⭐⭐ We value your feedback! Please take a moment to share your experience:\n" +
  "https://share.google/ujzcXkuoACDthNcBR\n" +
  "Thank you for choosing Healthcare Diagnostic Centre. We look forward to serving you again.";

/* ════════════════════════════════════════════════════════════
   "Is a printer connected?" detection + PDF fallback.

   IMPORTANT: no browser (mobile or desktop) exposes a JS API
   that reveals whether a physical printer is connected — that
   information is intentionally hidden for privacy/fingerprinting
   reasons. There is no `navigator.printers` or similar.

   The practical, widely-used substitute: treat phones/tablets as
   "no real printer available", since tapping Print on mobile just
   opens a share-sheet/print-preview that, for most users, has no
   physical printer behind it anyway. On those devices we skip the
   print dialog entirely and generate + download an actual PDF
   file using html2canvas + jsPDF. Desktop/laptop keeps the normal
   window.print() flow, since that reliably lists real printers.

   Install the two small libs this relies on:
     npm install html2canvas jspdf
   ════════════════════════════════════════════════════════════ */
const isMobileDevice = (): boolean => {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || (navigator as any).vendor || (window as any).opera || "";
  const mobileRegex = /android|iphone|ipod|iemobile|blackberry|bada|windows phone/i;
  // iPadOS 13+ reports itself as "Macintosh" — the touch-point check catches that case.
  const isIPadOs = /Macintosh/i.test(ua) && "ontouchend" in document && navigator.maxTouchPoints > 1;
  const isIpad = /ipad/i.test(ua);
  return mobileRegex.test(ua) || isIpad || isIPadOs;
};

/* Returns true when we should show the OS print dialog (desktop),
   false when we should generate a downloadable PDF instead (mobile). */
const shouldUsePrintDialog = (): boolean => !isMobileDevice();

/* ════════════════════════════════════════════════════════════
   FIXED: Reference Value Row — renders HTML properly
   Extra-spacing fix: scope CSS to .ref-val-html so that <p>,
   <div>, <br>, <ul>, <li> inside critical_info don't get
   browser-default 1em top/bottom margins that create huge gaps.
   ════════════════════════════════════════════════════════════ */
const REF_VAL_STYLE = `
  .ref-val-html {
    white-space: pre-wrap !important;
    word-break: break-word !important;
    overflow-wrap: anywhere !important;
    font-size: 11px !important;
    line-height: 1.35 !important;
  }
  .ref-val-html p,
  .ref-val-html div,
  .ref-val-html span,
  .ref-val-html ul,
  .ref-val-html ol,
  .ref-val-html li {
    margin: 0 !important;
    padding: 0 !important;
    line-height: 1.35 !important;
  }
  .ref-val-html table {
    border-collapse: collapse;
    margin: 0 !important;
  }
  .ref-val-html td,
  .ref-val-html th {
    padding: 1px 4px !important;
    margin: 0 !important;
  }
  .inline-result-html p,
  .inline-result-html div {
    margin: 0 !important;
    padding: 0 !important;
    display: inline !important;
  }
`;

const ReferenceValueRow: React.FC<{ info: TestInfo }> = ({ info }) => {
  // Only show when the "show_critical_info" toggle is explicitly enabled (=== 1)
  // AND there is actual content to display.
  if (!info.critical_info || Number(info.show_critical_info) !== 1) return null;

  return (
    <tr>
      <td style={tdBase} />
      <td style={tdBase} />
      <td
        colSpan={3}
        style={{
          ...tdBase,
          wordBreak: "break-word",
          overflowWrap: "anywhere",
        }}
      >
        <style>{REF_VAL_STYLE}</style>
        <div style={{ fontWeight: 700, marginBottom: 2 }}>REFERENCE VALUE</div>
        <div
          className="ref-val-html"
          style={{ marginTop: 0, lineHeight: 1.4, fontSize: 11 }}
          dangerouslySetInnerHTML={{ __html: info.critical_info }}
        />
      </td>
    </tr>
  );
};

/* ════════════════════════════════════════════════════════════
   FIXED: Interpretation Row — renders HTML properly with bullet
   ════════════════════════════════════════════════════════════ */
const InterpretationRow: React.FC<{ info: TestInfo }> = ({ info }) => {
  // Only show when the "show_interpretation" toggle is explicitly enabled (=== 1)
  // AND there is actual content to display.
  if (!info.interpretation || Number(info.show_interpretation) !== 1) return null;

  return (
    <tr>
      <td colSpan={5} style={tdBase}>
        <strong>INTERPRETATION:</strong>
        <div
          style={{ paddingLeft: 18, marginTop: 4, marginBottom: 0 }}
          dangerouslySetInnerHTML={{ __html: `<p style="margin:0;padding-left:18px">${info.interpretation}</p>` }}
        />
      </td>
    </tr>
  );
};

/* ════════════════════════════════════════════════════════════
   FIXED: Comments Row — renders HTML properly
   ════════════════════════════════════════════════════════════ */
const CommentsRow: React.FC<{ comments?: string }> = ({ comments }) => {
  if (!comments) return null;

  return (
    <tr>
      <td colSpan={5} style={tdBase}>
        <strong>COMMENTS:</strong>
        <div
          style={{ marginTop: 4 }}
          dangerouslySetInnerHTML={{ __html: comments }}
        />
      </td>
    </tr>
  );
};

const CriticalAndInterpretation: React.FC<{ info: TestInfo; comments?: string }> = ({ info, comments }) => {
  return (
    <>
      <ReferenceValueRow info={info} />
      <InterpretationRow info={info} />
      <CommentsRow comments={comments} />
    </>
  );
};

const TestReceipt: React.FC = () => {
  const { id }         = useParams<{ id: string }>();
  const navigate       = useNavigate();
  const [searchParams] = useSearchParams();
  const printRef       = useRef<HTMLDivElement>(null);

  const headerParam = searchParams.get("header") ?? "0";
  const [headerMode, setHeaderMode] = useState<string>(headerParam);

  useEffect(() => {
    setHeaderMode(headerParam);
  }, [headerParam]);

  const [data,      setData]      = useState<ReceiptData | null>(null);
  const [loading,   setLoading]   = useState(true);
  const [errMsg,    setErrMsg]    = useState("");
  const [rawJson,   setRawJson]   = useState("");
  const [showRaw,   setShowRaw]   = useState(false);
  const [imgErrors, setImgErrors] = useState<Record<string, boolean>>({});
  const [pdfGenerating, setPdfGenerating] = useState(false); // spinner state while building the print-fallback / download PDF
  const [sharing,       setSharing]       = useState(false); // spinner state while preparing the WhatsApp share

  useEffect(() => {
    if (!id) { setErrMsg("No ID in URL"); setLoading(false); return; }
    setLoading(true);
    setErrMsg("");
    setImgErrors({});
    API.get(`/report/test_receipt?bID=${id}&header=${headerParam}`)
      .then((res) => {
        setRawJson(JSON.stringify(res.data, null, 2));
        if (!res.data) { setErrMsg("Empty response"); return; }
        setData(res.data as ReceiptData);
      })
      .catch((err) => {
        const msg = err?.response?.data?.message || err?.message || "Network error";
        setErrMsg(`API error: ${msg}`);
        setRawJson(JSON.stringify(err?.response?.data ?? {}, null, 2));
      })
      .finally(() => setLoading(false));
  }, [id, headerParam]);

  const handleImgError = (src: string) =>
    setImgErrors(prev => ({ ...prev, [src]: true }));

  /* Desktop path: unchanged — opens a print-ready window and calls window.print()
     so the OS print dialog (with real printers) shows up. */
  const openPrintDialog = () => {
    const content = printRef.current?.innerHTML ?? "";
    const patientName = [pat.prefix, pat.P_name].filter(Boolean).join(" ").trim() || "Patient";
    // Filename shown by the browser's Save-as-PDF dialog: "<patient id>-<patient name>"
    const patientIdLabel = safe(pat.P_code, id ?? "");
    const docTitle = `${patientIdLabel}-${patientName}`;

    /* ════════════════════════════════════════════════════════════
       pick the @page margin per print based on resolvedHeaderMode.
       "0" = no header rendered -> letterhead stock -> bigger margins
       on EVERY page so pre-printed header/footer isn't overwritten.
       ════════════════════════════════════════════════════════════ */
    const pageMargin = resolvedHeaderMode === "0"
      ? NO_HEADER_PAGE_MARGIN
      : WITH_HEADER_PAGE_MARGIN;

    const win = window.open("", "_blank", "width=900,height=1100");
    if (!win) return;
    win.document.write(`<!DOCTYPE html><html><head>
      <title>${docTitle}</title>
      <style>
        *{box-sizing:border-box}
        html,body{margin:0;padding:0;background:#fff;color:#000}
        table{width:100%;border-collapse:collapse;table-layout:fixed;page-break-inside:auto}
        tr{page-break-inside:avoid;page-break-after:auto}
        td,th{border:1px solid #cfd4da;padding:3px 8px;font-size:12px;vertical-align:middle;font-family:Arial,Helvetica,sans-serif;line-height:1.3;word-break:break-word;overflow-wrap:anywhere}
        th{background:#F5F5F5;font-weight:700}
        img{max-width:100%}
        .no-print{display:none!important}
        .a4-sheet{width:100%;max-width:210mm;margin:0 auto;padding:8mm 10mm;background:#fff;box-sizing:border-box}
        .ref-val-html{white-space:pre-wrap!important;word-break:break-word!important;overflow-wrap:anywhere!important;font-size:11px!important;line-height:1.35!important}
        .ref-val-html p,.ref-val-html div,.ref-val-html span,.ref-val-html ul,.ref-val-html ol,.ref-val-html li{margin:0!important;padding:0!important;line-height:1.35!important}
        .ref-val-html table{border-collapse:collapse;margin:0!important}
        .ref-val-html td,.ref-val-html th{padding:1px 4px!important;margin:0!important}
        .inline-result-html p,.inline-result-html div{margin:0!important;padding:0!important;display:inline!important}
        @page{size:A4 portrait;margin:${pageMargin}}
        @media print{
          html,body{width:100%;margin:0;padding:0;background:#fff}
          .a4-sheet{width:100%;max-width:100%;margin:0;padding:0;box-shadow:none}
        }
      </style>
    </head><body>
      <div class="a4-sheet">${content}</div>
    </body></html>`);
    win.document.close();
    setTimeout(() => { win.print(); win.close(); }, 700);
  };

  /* Suggested filename (without extension) shared by download / share / print. */
  const getFileSafeName = (): string => {
    const patientName = [pat.prefix, pat.P_name].filter(Boolean).join(" ").trim() || "Patient";
    const patientIdLabel = safe(pat.P_code, id ?? "");
    return `${patientIdLabel}-${patientName}`.replace(/[^\w-]+/g, "_");
  };

  /* ════════════════════════════════════════════════════════════
     Shared PDF builder — used by Download, Share, AND the mobile
     "no printer" fallback for the Print button, so there is a
     single source of truth for pagination/margins.

     FIX (page-break bug): html2canvas rasterizes the report into
     ONE tall canvas with no concept of "row" or "paragraph" — it's
     just pixels. The previous version cut that canvas into page-
     sized chunks purely by pixel height, so a cut could (and did)
     land in the middle of a table row, a REFERENCE VALUE block, or
     any other line of text whenever the content didn't divide
     evenly into whole pages — exactly the broken-looking text at
     the bottom of one page / top of the next.

     FIX: before rasterizing, measure the CSS-pixel top/bottom of
     every "atomic" block in the live DOM — every direct <tr> of a
     top-level table (so a "Table"-type nested test grid, a
     REFERENCE VALUE row, an INTERPRETATION row etc. never get
     split across pages), plus any non-table top-level block (e.g.
     the header). Convert those to canvas-pixel coordinates once
     the canvas exists, and when choosing where a page should end,
     snap the cut back to the end of the last block that fits
     completely — never through the middle of one. If a single
     block is itself taller than a full page, it's placed as-is
     (unavoidable without splitting the block's own HTML).

     Returns the jsPDF instance UNSAVED so callers can either
     .save() it (download) or pull out a Blob/File (share).
     ════════════════════════════════════════════════════════════ */
  const buildReportPdf = async () => {
    if (!printRef.current) throw new Error("Nothing to export yet.");

    const { default: html2canvas } = await import("html2canvas");
    const { jsPDF } = await import("jspdf");

    // A4 page geometry in mm
    const pageWidthMm  = 210;
    const pageHeightMm = 297;

    // Same margins used for the printed @page rule, so the generated
    // PDF and the desktop print output match visually.
    const marginMm = resolvedHeaderMode === "0"
      ? { top: 30, bottom: 30, left: 10, right: 10 } // letterhead stock — see NO_HEADER_PAGE_MARGIN comment above
      : { top: 16, bottom: 14, left: 10, right: 10 };

    const contentWidthMm  = pageWidthMm  - marginMm.left - marginMm.right;
    const contentHeightMm = pageHeightMm - marginMm.top  - marginMm.bottom;

    // Render a full off-screen copy at the CONTENT width (margins are
    // applied later, per PDF page — not baked into this one image).
    const wrapper = document.createElement("div");
    wrapper.style.position = "fixed";
    wrapper.style.top = "0";
    wrapper.style.left = "-99999px"; // off-screen, but still laid out/rendered
    wrapper.style.width = `${contentWidthMm}mm`;
    wrapper.style.background = "#fff";
    wrapper.style.boxSizing = "border-box";
    wrapper.style.fontFamily = "Arial, Helvetica, sans-serif";
    wrapper.style.color = "#000";
    wrapper.innerHTML = printRef.current.innerHTML;
    document.body.appendChild(wrapper);

    /* ── Measure atomic content blocks in CSS px, before rasterizing ── */
    const wrapperRect = wrapper.getBoundingClientRect();
    const blocksCss: { top: number; bottom: number }[] = [];
    Array.from(wrapper.children).forEach((child) => {
      const el = child as HTMLElement;
      if (el.tagName === "TABLE") {
        el.querySelectorAll(":scope > tbody > tr").forEach((tr) => {
          const r = (tr as HTMLElement).getBoundingClientRect();
          blocksCss.push({ top: r.top - wrapperRect.top, bottom: r.bottom - wrapperRect.top });
        });
      } else {
        const r = el.getBoundingClientRect();
        blocksCss.push({ top: r.top - wrapperRect.top, bottom: r.bottom - wrapperRect.top });
      }
    });
    blocksCss.sort((a, b) => a.top - b.top);
    const wrapperCssHeight = wrapper.scrollHeight;

    const SCALE = 2; // ~192dpi, crisp enough for print while keeping file size reasonable
    const canvas = await html2canvas(wrapper, {
      scale: SCALE,
      useCORS: true,
      backgroundColor: "#ffffff",
    });
    document.body.removeChild(wrapper);

    // Pixels-per-mm derived from the actual rendered canvas, so the
    // slice heights line up exactly with the mm-based page geometry.
    const pxPerMm = canvas.width / contentWidthMm;
    const pageContentHeightPx = Math.max(1, Math.floor(contentHeightMm * pxPerMm));

    // Canvas px per CSS px — measured directly from the actual output
    // rather than assumed from SCALE, so it stays correct even if
    // html2canvas's effective device pixel ratio differs.
    const canvasPxPerCssPx = wrapperCssHeight > 0 ? canvas.height / wrapperCssHeight : SCALE;
    const blocksCanvasPx = blocksCss.map(b => ({
      top: b.top * canvasPxPerCssPx,
      bottom: b.bottom * canvasPxPerCssPx,
    }));

    /* Snap an "ideal" page-end pixel back to the end of the last block
       that fits fully on the current page, so a page break never lands
       inside a table row / reference-value block / paragraph. If the
       straddling block starts at or before the current page's start
       (i.e. it's taller than one whole page on its own), fall back to
       the ideal cut — there's no safe break inside that one block. */
    const snapBreak = (idealEnd: number, pageStart: number): number => {
      const straddling = blocksCanvasPx.find(b => b.top < idealEnd - 0.5 && b.bottom > idealEnd + 0.5);
      if (!straddling) return idealEnd;
      return straddling.top > pageStart ? straddling.top : idealEnd;
    };

    const pdf = new jsPDF("p", "mm", "a4");
    let cursorPx  = 0;
    let pageIndex = 0;

    while (cursorPx < canvas.height - 0.5) {
      const idealEnd = Math.min(cursorPx + pageContentHeightPx, canvas.height);
      const breakPx = idealEnd >= canvas.height - 0.5
        ? canvas.height
        : snapBreak(idealEnd, cursorPx);
      const sliceHeightPx = Math.max(1, Math.round(breakPx - cursorPx));

      // Crop this page's slice onto its own canvas so we can place it
      // at a fixed top-margin offset without any bleed from other pages.
      const sliceCanvas = document.createElement("canvas");
      sliceCanvas.width  = canvas.width;
      sliceCanvas.height = sliceHeightPx;
      const ctx = sliceCanvas.getContext("2d");
      if (ctx) {
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, sliceCanvas.width, sliceCanvas.height);
        ctx.drawImage(
          canvas,
          0, cursorPx, canvas.width, sliceHeightPx,
          0, 0, canvas.width, sliceHeightPx
        );
      }

      const sliceImgData = sliceCanvas.toDataURL("image/jpeg", 0.95);
      const sliceHeightMm = sliceHeightPx / pxPerMm;

      if (pageIndex > 0) pdf.addPage();

      // Every page gets the SAME top margin, and content never runs
      // past (pageHeight - bottom margin) — consistent breathing room
      // at the top AND bottom of every page, including mid-document ones.
      pdf.addImage(
        sliceImgData, "JPEG",
        marginMm.left, marginMm.top,
        contentWidthMm, sliceHeightMm
      );

      cursorPx  += sliceHeightPx;
      pageIndex += 1;
    }

    return pdf;
  };

  /* Mobile / "no printer" path for the Print button — builds the PDF
     and saves it straight away, same as before. */
  const downloadAsPdf = async () => {
    if (pdfGenerating) return;
    setPdfGenerating(true);
    try {
      const pdf = await buildReportPdf();
      pdf.save(`${getFileSafeName()}.pdf`);
    } catch (err) {
      console.error("PDF generation failed:", err);
      alert("Could not generate the PDF. Please try again.");
    } finally {
      setPdfGenerating(false);
    }
  };

  /* Entry point used by the toolbar's Print/Save button: routes to the
     OS print dialog on desktop, or straight to a PDF download on
     mobile/tablet where a real connected printer usually isn't available. */
  const handlePrint = () => {
    if (pdfGenerating) return;
    if (shouldUsePrintDialog()) {
      openPrintDialog();
    } else {
      downloadAsPdf();
    }
  };

  /* ════════════════════════════════════════════════════════════
     NEW: explicit Download button — always generates and saves the
     PDF directly (no print dialog), on desktop AND mobile alike.
     ════════════════════════════════════════════════════════════ */
  const handleDownload = async () => {
    if (pdfGenerating) return;
    setPdfGenerating(true);
    try {
      const pdf = await buildReportPdf();
      pdf.save(`${getFileSafeName()}.pdf`);
    } catch (err) {
      console.error("PDF generation failed:", err);
      alert("Could not generate the PDF. Please try again.");
    } finally {
      setPdfGenerating(false);
    }
  };

  /* ════════════════════════════════════════════════════════════
     NEW: Share button — sends the report to WhatsApp with the
     pre-filled message text below.

     WhatsApp has no public web link that accepts a file attachment
     (wa.me / api.whatsapp.com only accept a "text" param), so:
       1. If the browser's native Share Sheet supports sharing files
          (navigator.canShare({ files })) — common on mobile Chrome/
          Safari, and increasingly on desktop Chrome/Edge — we hand
          the actual PDF + message straight to it. The user then just
          picks WhatsApp (or any app) from the OS share sheet.
       2. Otherwise (older/unsupported browsers) we download the PDF
          for the user and open WhatsApp's contact picker with the
          message pre-filled. The user picks a contact, then attaches
          the already-downloaded PDF from their device inside the chat.
     ════════════════════════════════════════════════════════════ */
  const handleShare = async () => {
    if (sharing) return;
    setSharing(true);
    try {
      const pdf = await buildReportPdf();
      const fileSafeName = getFileSafeName();
      const blob = pdf.output("blob") as Blob;
      const file = new File([blob], `${fileSafeName}.pdf`, { type: "application/pdf" });

      const nav = navigator as Navigator & {
        canShare?: (data?: ShareData) => boolean;
        share?: (data: ShareData) => Promise<void>;
      };

      if (nav.canShare && nav.share && nav.canShare({ files: [file] })) {
        try {
          await nav.share({
            files: [file],
            text: WHATSAPP_SHARE_MESSAGE,
            title: fileSafeName,
          });
          return; // user completed (or is completing) the native share flow
        } catch (shareErr: any) {
          if (shareErr?.name === "AbortError") return; // user cancelled — do nothing further
          // otherwise fall through to the WhatsApp web fallback below
        }
      }

      // Fallback: download the PDF, then open WhatsApp with the message
      // pre-filled so the user can pick a contact and attach the file.
      pdf.save(`${fileSafeName}.pdf`);
      const waUrl = `https://wa.me/?text=${encodeURIComponent(WHATSAPP_SHARE_MESSAGE)}`;
      window.open(waUrl, "_blank");
    } catch (err) {
      console.error("Share failed:", err);
      alert("Could not prepare the report for sharing. Please try again.");
    } finally {
      setSharing(false);
    }
  };

  /* ════════════════════════════════════════════════════════════
     FIX 1: Merge consecutive groups with same category_name
     so one heading covers all its tests.
     ════════════════════════════════════════════════════════════ */
  const mergedEntries = React.useMemo(() => {
    const entries = data?.entries ?? [];
    if (!entries.length) return [];
    const merged: CategoryGroup[] = [];
    for (const group of entries) {
      const last = merged[merged.length - 1];
      if (last && last.category_name === group.category_name) {
        last.tests = [...(last.tests ?? []), ...(group.tests ?? [])];
      } else {
        merged.push({ ...group, tests: [...(group.tests ?? [])] });
      }
    }
    return merged;
  }, [data?.entries]);

  if (loading) return (
    <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 300, gap: 12 }}>
      <div className="spinner-border text-primary" />
      <span>Loading report…</span>
    </div>
  );

  if (errMsg || !data) return (
    <div style={{ padding: 24 }}>
      <div className="alert alert-danger"><strong>Error:</strong> {errMsg || "No data returned"}</div>
      {rawJson && (
        <>
          <button className="btn btn-sm btn-outline-secondary mb-2" onClick={() => setShowRaw(x => !x)}>
            {showRaw ? "Hide" : "Show"} raw API response
          </button>
          {showRaw && (
            <pre style={{ background: "#1e1e1e", color: "#d4d4d4", padding: 12, borderRadius: 4, fontSize: 11, overflow: "auto", maxHeight: 400 }}>
              {rawJson}
            </pre>
          )}
        </>
      )}
      <button className="btn btn-secondary btn-sm mt-3" onClick={() => navigate(-1)}>← Go back</button>
    </div>
  );

  const lab: LabInfo   = data.labInfo ?? data.orgInfo ?? {};
  const bill: BillData = data.billData ?? {};
  const pat: Patient   = data.patient  ?? {};
  const terms          = data.terms    ?? [];
  const reference      = data.reference ?? "";
  const billNo         = bill.bill_no ?? "";

  const resolvedHeaderMode = headerMode ?? data.headerMode ?? headerParam ?? "0";

  const headerImageSrc: string = (() => {
    if (resolvedHeaderMode === "1") return headerLogoUrl(lab.header_logo_1);
    if (resolvedHeaderMode === "2") return headerLogoUrl(lab.header_logo_2);
    return "";
  })();

  const headerImgFailed  = !!(headerImageSrc && imgErrors[headerImageSrc]);
  const showHeaderImage  =
    (resolvedHeaderMode === "1" || resolvedHeaderMode === "2") &&
    !!headerImageSrc &&
    !headerImgFailed;

  const headerBadgeLabel =
    resolvedHeaderMode === "1" ? "🖼 Header 1"
    : resolvedHeaderMode === "2" ? "🖼 Header 2"
    : "📄 Text Header";
  const headerBadgeClass =
    resolvedHeaderMode !== "0" && !headerImgFailed ? "bg-success"
    : resolvedHeaderMode !== "0" && headerImgFailed ? "bg-warning text-dark"
    : "bg-secondary";

  /* ════════════════════════════════════════════════════════════
     Same margin logic used for the on-screen @media print
     rule below, so a direct browser Ctrl+P (bypassing the
     toolbar "Print / Save PDF" button) still reserves the right
     amount of top/bottom space on every page for letterhead.
     ════════════════════════════════════════════════════════════ */
  const screenPrintPageMargin = resolvedHeaderMode === "0"
    ? NO_HEADER_PAGE_MARGIN
    : WITH_HEADER_PAGE_MARGIN;

  return (
    <div className="container-fluid py-3">

      {/* ---- Toolbar ---- */}
      <div className="d-flex gap-2 mb-3 no-print flex-wrap">
        <button className="btn btn-secondary btn-sm" onClick={() => navigate(-1)}>← Back</button>
        <div className="ms-auto d-flex align-items-center gap-2 flex-wrap">
          <select
            className="form-select form-select-sm"
            style={{ width: "auto", minWidth: 125 }}
            value={resolvedHeaderMode}
            onChange={(e) => setHeaderMode(e.target.value)}
            title="Header Style"
          >
            <option value="0">📄 No Header</option>
            <option value="1">🖼 Header 1</option>
            <option value="2">🖼 Header 2</option>
          </select>
          <button
            className="btn btn-primary btn-sm"
            onClick={handlePrint}
            disabled={pdfGenerating || sharing}
          >
            {pdfGenerating
              ? "⏳ Generating…"
              : shouldUsePrintDialog()
                ? "🖨 Print / Save PDF"
                : "⬇ Download PDF"}
          </button>
          <button
            className="btn btn-outline-primary btn-sm"
            onClick={handleDownload}
            disabled={pdfGenerating || sharing}
            title="Download this report as a PDF"
          >
            {pdfGenerating ? "⏳ Preparing…" : "⬇ Download"}
          </button>
          <button
            className="btn btn-success btn-sm"
            onClick={handleShare}
            disabled={pdfGenerating || sharing}
            title="Share this report on WhatsApp"
          >
            {sharing ? "⏳ Preparing…" : "📤 Share on WhatsApp"}
          </button>
        </div>
      </div>

      {headerImgFailed && (
        <div className="alert alert-warning py-2 no-print" style={{ fontSize: 12 }}>
          ⚠ Header image could not be loaded: <code>{headerImageSrc}</code>
        </div>
      )}

      {showRaw && (
        <pre className="no-print" style={{ background: "#1e1e1e", color: "#d4d4d4", padding: 12, borderRadius: 4, fontSize: 11, overflow: "auto", maxHeight: 300, marginBottom: 16 }}>
          {rawJson}
        </pre>
      )}

      {/* ---- A4 Sheet ---- */}
      <style>{`
        .a4-receipt-outer {
          padding: 24px 0;
          min-height: calc(100vh - 100px);
        }
        .a4-sheet-screen {
          width: ${A4_WIDTH_MM}mm;
          min-height: ${A4_HEIGHT_MM}mm;
          margin: 0 auto;
          background: #fff;
          box-shadow: 0 4px 20px rgba(0,0,0,.15);
          padding: 10mm 12mm;
          box-sizing: border-box;
          border-radius: 2px;
        }
        @media (max-width: 900px) {
          .a4-receipt-outer {
            overflow-x: auto;
            -webkit-overflow-scrolling: touch;
            padding: 8px 4px;
            background: transparent;
          }
          .a4-sheet-screen {
            width: 100% !important;
            min-height: auto;
            padding: 12px 8px;
            box-shadow: none;
          }
        }
        @media print {
          .a4-receipt-outer {
            background: #fff;
            padding: 0;
          }
          .a4-sheet-screen {
            width: 100%;
            margin: 0;
            padding: 0;
            box-shadow: none;
            min-height: auto;
          }
          @page { size: A4 portrait; margin: ${screenPrintPageMargin}; }
        }
      `}</style>
      <div className="a4-receipt-outer">
      <div className="a4-sheet-screen">
        <div ref={printRef} style={{ fontFamily: "Arial, Helvetica, sans-serif", color: "#000", width: "100%" }}>

          {/* ══════════════════════ HEADER ══════════════════════ */}
          {showHeaderImage ? (
            <div style={{ textAlign: "center", marginBottom: 10 }}>
              <img
                src={headerImageSrc}
                alt="letterhead"
                style={{ maxWidth: "100%", maxHeight: 140, display: "block", margin: "0 auto" }}
                onError={() => handleImgError(headerImageSrc)}
              />
            </div>
          ) : resolvedHeaderMode === "0" && lab.show_report_header === 1 && lab.report_header ? (
            <div style={{ marginBottom: 10 }}>
              <h6
                style={{ color: "#000", fontWeight: 700, textAlign: "left", margin: 0 }}
                dangerouslySetInnerHTML={{ __html: lab.report_header }}
              />
            </div>
          ) : resolvedHeaderMode !== "0" ? (
            <div style={{ textAlign: "center", marginBottom: 10 }}>
              <div style={{ fontSize: 20, fontWeight: 700, color: ACCENT, letterSpacing: 1 }}>
                {safe(lab.lab_name, "Laboratory")}
              </div>
              {lab.location && (
                <div style={{ fontSize: 12, color: "#333", marginTop: 2 }}>{lab.location}</div>
              )}
              <div style={{ fontSize: 11, color: "#333", marginTop: 3, display: "flex", gap: 20, justifyContent: "center" }}>
                {lab.phone && <span>📞 {lab.phone}</span>}
                {lab.email && <span>✉ {lab.email}</span>}
              </div>
            </div>
          ) : (
            /* No header mode — the @page margin above already reserves
               top/bottom space for the letterhead on every printed page,
               so we no longer need this small fixed on-screen spacer to
               do the job. Kept at a small value purely for on-screen
               breathing room before the patient table. */
            <div style={{ height: 16 }} />
          )}


          {/* ══════════════════════ PATIENT INFO TABLE ══════════════════════ */}
          <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
            <colgroup>
              <col style={{ width: "8%" }} />
              <col style={{ width: "48%" }} />
              <col style={{ width: "22%" }} />
              <col style={{ width: "22%" }} />
            </colgroup>

            <tbody>
              <tr>
                <td style={labelCell}>Patient ID</td>
                <td style={valueCell}>{safe(pat.P_code)}</td>
                <td style={label1Cell}>ID : {billNo}</td>
                <td style={{ ...tdBase, padding: "2px 6px" }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-start", gap: 10 }}>
                    {billNo && <BarcodeDisplay value={billNo} width={120} height={24} />}
                  </div>
                </td>
              </tr>

              <tr>
                <td style={labelCell}>Name</td>
                <td style={valueCell}>
                  {/*
                    FIX: prefix + single dot + name.
                    Some prefixes in the data already include a trailing
                    dot (e.g. "Mr.", "Dr."), which combined with an
                    appended dot produced "Mr.. Name". Strip any
                    trailing dot(s) from the prefix first, THEN add
                    exactly one dot, so the result is always a single
                    dot regardless of how the source data stored it.
                  */}
                  {(() => {
                    const prefixClean = pat.prefix ? pat.prefix.trim().replace(/\.+$/, "") : "";
                    return [prefixClean ? `${prefixClean}.` : "", safe(pat.P_name)]
                      .filter(Boolean)
                      .join(" ");
                  })()}
                  {pat.id_number && (
                    <span style={{ fontWeight: 400 }}>
                      {" "}(
                      {pat.id_card_type
                        ? pat.id_card_type.replace(/\b\w/g, c => c.toUpperCase())
                        : "ID"}
                      : {safe(pat.id_number)})
                    </span>
                  )}
                </td>
                <td style={label1Cell}>Sample Collected On</td>
                <td style={{ ...tdBase, whiteSpace: "nowrap" }}>
                  {fmtDate(bill.entry_date)} {fmtTime(bill.entry_time)}
                </td>
              </tr>

              <tr>
                <td style={labelCell}>Age / Sex</td>
                <td style={valueCell}>
                  {`${safe(pat.age)} ${safe(pat.age_type)} / ${safe(pat.gender)}`}
                </td>
                <td style={label1Cell}>Report Released On</td>
                <td style={{ ...tdBase, whiteSpace: "nowrap" }}>
                  {/* Both date and time come from the persisted report save timestamp. */}
                  {fmtDate(bill.modified)} {fmtTime(bill.modified_time)}
                </td>
              </tr>

              <tr>
                <td style={labelCell}>Ref. By</td>
                <td style={valueCell} colSpan={3}>{safe(reference)}</td>
              </tr>
            </tbody>
          </table>

          {/* ══════════════════════ REPORT TITLE BAR ══════════════════════ */}
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <tbody>
              <tr>
                <td style={{ ...tdBase, textAlign: "center", padding: "8px 0"}}>
                  <div style={{ fontSize: 16, fontWeight: 700, letterSpacing: 0.5}}>
                    LABORATORY REPORT
                  </div>
                </td>
              </tr>
            </tbody>
          </table>

          {/* ══════════════════════ RESULTS TABLE ══════════════════════ */}
          <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
            <colgroup>
              <col style={{ width: COLW.sample }} />
              <col style={{ width: COLW.investigation }} />
              <col style={{ width: COLW.result }} />
              <col style={{ width: COLW.units }} />
              <col style={{ width: COLW.range }} />
            </colgroup>

            <tbody>
              <tr>
                <th style={{ ...thBase, textAlign: "center" }}>SAMPLE</th>
                <th style={thBase}>INVESTIGATION</th>
                <th style={thBase}>RESULT</th>
                <th style={thBase}>UNITS</th>
                <th style={thBase}>NORMAL RANGE</th>
              </tr>

              {mergedEntries.length === 0 ? (
                <tr>
                  <td colSpan={5} style={{ ...tdBase, textAlign: "center", color: "#888", padding: 24 }}>
                    No test results found for this entry.
                  </td>
                </tr>
              ) : mergedEntries.map((group, gi) => {
                const tests = group.tests ?? [];
                return (
                  <React.Fragment key={gi}>
                    {group.category_name && (
                      <tr>
                        <td colSpan={5} style={{ ...tdBase, fontWeight: 700, textAlign: "center" }}>
                          {group.category_name}
                        </td>
                      </tr>
                    )}

                    {tests.map((test, ti) => {
                      const info     = test.test_info ?? {};
                      const typeTest = info.type_test ?? "Normal";
                      const flag     = getResultFlag(test.test_result, info.lower_limit, info.upper_limit);

                      /* ════════════════════════════════════════════════════════════
                         FIX: Sub Heading Row — only render if sub_heading is present,
                         changed from previous test, AND typeTest is not itself "Sub Heading"
                         (so we don't render duplicate lines for Sub Heading type).
                         ════════════════════════════════════════════════════════════ */
                      const prevSub = ti > 0 ? tests[ti - 1].sub_heading : undefined;
                      const showSub = test.sub_heading && test.sub_heading !== prevSub && typeTest !== "Sub Heading";

                      const SubRow = showSub ? (
                        <tr key={`sub-${gi}-${ti}`}>
                          <td colSpan={5} style={{ ...tdBase, fontWeight: 700 }}>
                            {test.sub_heading}
                          </td>
                        </tr>
                      ) : null;

                      const sampleText = safe(info.sample_type);

                      const SampleCell = (
                        <td style={{ ...tdBase, textAlign: "center", verticalAlign: "middle" }}>
                          {sampleText}
                          {info.method && (
                            <div style={methodSubText}>{info.method}</div>
                          )}
                        </td>
                      );

                      const InvestigationCell = (
                        <td style={tdBase}>
                          {safe(info.test_name)}
                        </td>
                      );

                      if (typeTest === "Sub Heading") {
                        // Primary heading text: what the user typed in the Sub Heading field
                        // during result entry (test.sub_heading), then the test_result value,
                        // then the test name as last fallback.
                        const enteredHeading = safe(test.sub_heading);
                        const resultVal      = safe(test.test_result);
                        const headingText    = enteredHeading || resultVal || safe(info.test_name);

                        // Only show H/L flag when there is a numeric test_result to compare
                        const subFlag = resultVal
                          ? getResultFlag(resultVal, info.lower_limit, info.upper_limit)
                          : null;

                        return (
                          <React.Fragment key={ti}>
                            {SubRow}
                            <tr>
                              <td colSpan={5} style={{ ...tdBase, fontWeight: 700 }}>
                                {headingText}
                                {/* Show numeric result alongside the heading if it differs from the heading */}
                                {resultVal && resultVal !== headingText && (
                                  <span style={{ fontWeight: 400, marginLeft: 8 }}>
                                    {renderFlaggedResult(resultVal, subFlag, lab)}
                                  </span>
                                )}
                              </td>
                            </tr>
                            <CriticalAndInterpretation info={info} comments={info.comments} />
                          </React.Fragment>
                        );
                      }

                      if (typeTest === "Table") {
                        const heads   = [test.head_1, test.head_2, test.head_3, test.head_4, test.head_5, test.head_6].filter(Boolean);
                        const results = [test.result_1, test.result_2, test.result_3, test.result_4, test.result_5, test.result_6].slice(0, heads.length);
                        // If no table headers exist (dropdown-only test), fall back to
                        // displaying test_result directly — same as a Normal row.
                        const isDropdownOnly = heads.length === 0;
                        const dropdownFlag = isDropdownOnly
                          ? getResultFlag(test.test_result, info.lower_limit, info.upper_limit)
                          : null;
                        return (
                          <React.Fragment key={ti}>
                            {SubRow}
                            <tr>
                              {SampleCell}
                              {InvestigationCell}
                              {isDropdownOnly ? (
                                <>
                                  <td style={{ ...tdBase, fontWeight: 400 }}>
                                    {renderFlaggedResult(safe(test.test_result), dropdownFlag, lab)}
                                  </td>
                                  <td style={tdBase}>{safe(info.units)}</td>
                                  <td style={tdBase}>
                                    {(info.lower_limit && info.upper_limit)
                                      ? `${info.lower_limit} – ${info.upper_limit}`
                                      : info.lower_limit || info.upper_limit || ""}
                                  </td>
                                </>
                              ) : (
                                <td colSpan={3} style={{ ...tdBase, padding: 0 }}>
                                  <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
                                    <thead>
                                      <tr>{heads.map((h, i) => (
                                        <th key={i} style={{ border: BORDER, padding: "4px 8px", background: HEAD_BG, fontWeight: 700, textAlign: "left", wordBreak: "break-word", overflowWrap: "anywhere" }}>{h}</th>
                                      ))}</tr>
                                    </thead>
                                    <tbody>
                                      <tr>{results.map((r, i) => (
                                        <td key={i} style={{ border: BORDER, padding: "4px 8px", wordBreak: "break-word", overflowWrap: "anywhere" }}>{safe(r)}</td>
                                      ))}</tr>
                                    </tbody>
                                  </table>
                                </td>
                              )}
                            </tr>
                            <CriticalAndInterpretation info={info} comments={info.comments} />
                          </React.Fragment>
                        );
                      }

                      if (typeTest === "Image") {
                        const testImages = test.images ?? [];
                        return (
                          <React.Fragment key={ti}>
                            {SubRow}
                            <tr>
                              {SampleCell}
                              <td style={tdBase} colSpan={4}>
                                {safe(info.test_name)}
                                {info.method && (
                                  <div style={methodSubText}>{info.method}</div>
                                )}
                              </td>
                            </tr>
                            {test.paragraph && (
                              <tr>
                                <td colSpan={5} style={tdBase} dangerouslySetInnerHTML={{ __html: test.paragraph }} />
                              </tr>
                            )}
                            {testImages.map(img => {
                              const imgSrc = assetUrl(img.document_path);
                              return (
                                <tr key={img.document_id}>
                                  <td colSpan={5} style={{ ...tdBase, textAlign: "center" }}>
                                    {!imgErrors[imgSrc] ? (
                                      <img
                                        src={imgSrc}
                                        alt="test"
                                        width={250}
                                        height={100}
                                        style={{ objectFit: "contain" }}
                                        onError={() => handleImgError(imgSrc)}
                                      />
                                    ) : (
                                      <span style={{ color: "#999", fontStyle: "italic" }}>
                                        [Image unavailable: {img.document_path}]
                                      </span>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                            <CriticalAndInterpretation info={info} comments={info.comments} />
                          </React.Fragment>
                        );
                      }

                      if (typeTest === "Paragraph") {
                        const paraContent = safe(test.paragraph) || safe(test.test_result);
                        return (
                          <React.Fragment key={ti}>
                            {SubRow}
                            <tr>
                              {SampleCell}
                              {InvestigationCell}
                              <td style={tdBase} colSpan={3}>
                                <div
                                  className="inline-result-html"
                                  dangerouslySetInnerHTML={{ __html: paraContent }}
                                />
                              </td>
                            </tr>
                            <CriticalAndInterpretation info={info} comments={info.comments} />
                          </React.Fragment>
                        );
                      }

                      // Date/time metadata must not populate an unentered normal result.
                      const rawResult = typeTest === "Date"
                        ? safe(test.date)
                        : typeTest === "Time"
                          ? safe(test.time)
                          : safe(test.test_result);
                      const refRange = (info.lower_limit && info.upper_limit)
                        ? `${info.lower_limit} – ${info.upper_limit}`
                        : info.lower_limit || info.upper_limit || "";

                      return (
                        <React.Fragment key={ti}>
                          {SubRow}
                          <tr>
                            {SampleCell}
                            {InvestigationCell}
                            <td
                              style={{
                                ...tdBase,
                                fontWeight: 400,
                              }}
                            >
                              {renderFlaggedResult(rawResult, flag, lab)}
                            </td>
                            <td style={tdBase}>{safe(info.units)}</td>
                            <td style={tdBase}>{refRange}</td>
                          </tr>
                          <CriticalAndInterpretation info={info} comments={info.comments} />
                        </React.Fragment>
                      );
                    })}
                  </React.Fragment>
                );
              })}

              {/* ── Terms ── */}
              {terms.length > 0 && (
                <tr>
                  <td colSpan={5} style={tdBase}>
                    <strong>Terms &amp; Conditions:</strong>
                    <ul style={{ paddingLeft: 18, marginTop: 4, marginBottom: 0 }}>
                      {terms.map(t => <li key={t.id}>{t.description}</li>)}
                    </ul>
                  </td>
                </tr>
              )}

              {/* ── Signatory ── */}
              <tr>
                <td colSpan={5} style={{ ...tdBase, textAlign: "right", paddingTop: 45, paddingBottom: 12 }}>
                  Authorized Signatory
                </td>
              </tr>

              {/* ── Footer ── */}
              <tr>
                <td colSpan={5} style={{ ...tdBase, textAlign: "center", fontWeight: 700, paddingTop: 30, paddingBottom: 20 }}>
                  End Of Report
                  {lab.report_footer && (
                    <div style={{ fontWeight: 400, marginTop: 8 }}
                      dangerouslySetInnerHTML={{ __html: lab.report_footer }} />
                  )}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      </div>
    </div>
  );
};

export default TestReceipt;